import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getSupabaseClient } from "../../../lib/supabaseClient";
import { useCustomerAuth } from "../../../contexts/CustomerAuthContext";
import CustomerLayout from "../components/CustomerLayout";
import type { Itinerary, ItineraryStatus } from "../../../store/types";
import { fetchLatestItineraryNotices } from "../../../api/itineraryRelayApi";

interface BookingInfo {
  order_id: string;
  order_number: string;
  order_status: string;
  selected_city: string;
  shoot_date: string;
  start_time: string;
  end_time: string;
  special_requests: string;
  photographer_name: string | null;
  itinerary: Itinerary | null;
  relayNotice: string | null;
}

const ORDER_STATUS_LABELS: Record<string, { label: string; cls: string }> = {
  pending: { label: "待處理", cls: "bg-amber-100 text-amber-700" },
  confirmed: { label: "已確認", cls: "bg-green-100 text-green-700" },
  completed: { label: "已完成", cls: "bg-[#C8B8A6]/20 text-[#7A6B5A]" },
  cancelled: { label: "已取消", cls: "bg-red-100 text-red-600" },
};

// Customer-safe itinerary status wording
const ITINERARY_STATUS_LABELS: Record<ItineraryStatus, { label: string; cls: string }> = {
  draft: { label: "攝影服務安排中", cls: "bg-[#F0EDEA] text-[#7A6B5A]" },
  confirmed: { label: "攝影服務已確認", cls: "bg-emerald-50 text-emerald-700" },
  updated: { label: "攝影服務資訊已更新", cls: "bg-teal-50 text-teal-700" },
  cancelled: { label: "攝影服務已取消", cls: "bg-red-50 text-red-600" },
};

function formatLocalTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("zh-TW", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function CustomerDashboardPage() {
  const { session, isLoading: authLoading } = useCustomerAuth();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<BookingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !session) navigate("/customer/login", { replace: true });
  }, [session, authLoading, navigate]);

  const fetchBooking = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setError(null);
    try {
      const supabase = getSupabaseClient();

      const { data: order, error: orderErr } = await supabase
        .from("orders")
        .select("id,order_number,order_status,selected_city,shoot_date,start_time,end_time,special_requests")
        .eq("id", session.order_id)
        .maybeSingle();

      if (orderErr) throw orderErr;
      if (!order) throw new Error("訂單不存在");

      const { data: bc } = await supabase
        .from("booking_confirmations")
        .select("photographer_id,confirmation_status")
        .eq("order_id", session.order_id)
        .eq("confirmation_status", "confirmed")
        .maybeSingle();

      let photographerName: string | null = null;
      if (bc?.photographer_id) {
        const { data: ph } = await supabase
          .from("photographers")
          .select("display_name")
          .eq("id", bc.photographer_id)
          .maybeSingle();
        photographerName = ph?.display_name ?? null;
      }

      const { data: itin } = await supabase
        .from("itineraries")
        .select("*")
        .eq("order_id", session.order_id)
        .maybeSingle();

      // Relay notice for customer
      const noticeMap = await fetchLatestItineraryNotices([session.order_id], "customer");

      setBooking({
        order_id: (order as { id: string }).id,
        order_number: order.order_number,
        order_status: order.order_status,
        selected_city: order.selected_city,
        shoot_date: order.shoot_date,
        start_time: order.start_time ?? "",
        end_time: order.end_time ?? "",
        special_requests: order.special_requests ?? "",
        photographer_name: photographerName,
        itinerary: itin as Itinerary | null,
        relayNotice: noticeMap.get(session.order_id) ?? null,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "載入失敗");
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  if (authLoading) return null;

  const statusInfo = booking
    ? (ORDER_STATUS_LABELS[booking.order_status] ?? { label: booking.order_status, cls: "bg-gray-100 text-gray-600" })
    : null;

  const itinStatusInfo = booking?.itinerary
    ? (ITINERARY_STATUS_LABELS[booking.itinerary.status] ?? ITINERARY_STATUS_LABELS.draft)
    : null;

  return (
    <CustomerLayout title="我的預訂">
      {loading ? (
        <div className="flex items-center justify-center h-48 text-[#2C2C2C]/40 text-sm font-light">載入中...</div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center h-48 gap-3">
          <p className="text-sm text-red-500 font-light">{error}</p>
          <button onClick={fetchBooking} className="text-sm text-[#2C2C2C]/60 underline cursor-pointer">重試</button>
        </div>
      ) : booking && (
        <div className="flex flex-col gap-4">
          {/* Booking summary card */}
          <div className="bg-white rounded-xl border border-[#D9D9D9]/60 p-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-sm font-light text-[#2C2C2C]/40 mb-0.5">訂單編號</h2>
                <p className="text-base font-light text-[#2C2C2C] tracking-wide">{booking.order_number}</p>
              </div>
              {statusInfo && (
                <span className={`px-2.5 py-1 rounded-full text-xs font-light ${statusInfo.cls}`}>{statusInfo.label}</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm border-t border-[#D9D9D9]/40 pt-4">
              <div>
                <p className="text-[11px] font-light text-[#2C2C2C]/40 mb-0.5">拍攝城市</p>
                <p className="font-light text-[#2C2C2C]">{booking.selected_city}</p>
              </div>
              <div>
                <p className="text-[11px] font-light text-[#2C2C2C]/40 mb-0.5">拍攝日期</p>
                <p className="font-light text-[#2C2C2C]">{booking.shoot_date}</p>
              </div>
              {booking.start_time && (
                <div>
                  <p className="text-[11px] font-light text-[#2C2C2C]/40 mb-0.5">拍攝時間</p>
                  <p className="font-light text-[#2C2C2C]">{booking.start_time} – {booking.end_time}</p>
                </div>
              )}
              <div>
                <p className="text-[11px] font-light text-[#2C2C2C]/40 mb-0.5">負責攝影師</p>
                <p className="font-light text-[#2C2C2C]">{booking.photographer_name ?? "媒合中..."}</p>
              </div>
            </div>
            {booking.special_requests && (
              <div className="border-t border-[#D9D9D9]/40 pt-3 mt-3">
                <p className="text-[11px] font-light text-[#2C2C2C]/40 mb-0.5">特殊需求</p>
                <p className="text-sm font-light text-[#2C2C2C]/70">{booking.special_requests}</p>
              </div>
            )}
          </div>

          {/* Relay system notice — secondary callout */}
          {booking.relayNotice && (
            <div className="flex items-start gap-3 px-4 py-3 bg-teal-50 border border-teal-100 rounded-xl">
              <div className="w-5 h-5 flex items-center justify-center text-teal-500 mt-0.5 shrink-0">
                <i className="ri-notification-3-line text-base" />
              </div>
              <div>
                <p className="text-xs font-light text-teal-600 mb-0.5">平台通知</p>
                <p className="text-sm font-light text-teal-800 leading-relaxed">{booking.relayNotice}</p>
              </div>
            </div>
          )}

          {/* Official itinerary card — primary data source */}
          <div className="bg-white rounded-xl border border-[#D9D9D9]/60 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-light text-[#2C2C2C]/50 uppercase tracking-wider">攝影服務集合資訊</h3>
              {itinStatusInfo && (
                <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-light ${itinStatusInfo.cls}`}>
                  {itinStatusInfo.label}
                </span>
              )}
            </div>

            {booking.itinerary ? (
              <div className="flex flex-col gap-4">
                <div>
                  <p className="text-[11px] font-light text-[#2C2C2C]/40 mb-0.5">集合地點</p>
                  <p className="text-sm font-light text-[#2C2C2C]">{booking.itinerary.meeting_point || "—"}</p>
                </div>
                <div>
                  <p className="text-[11px] font-light text-[#2C2C2C]/40 mb-0.5">集合時間</p>
                  <p className="text-sm font-light text-[#2C2C2C]">{formatLocalTime(booking.itinerary.meeting_time)}</p>
                </div>
                {booking.itinerary.map_url && (
                  <div>
                    <p className="text-[11px] font-light text-[#2C2C2C]/40 mb-1.5">地圖</p>
                    <a
                      href={booking.itinerary.map_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#F7F6F3] rounded-lg text-sm font-light text-[#2C2C2C]/70 hover:text-[#2C2C2C] transition-colors cursor-pointer border border-[#D9D9D9]/60"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-2-line" />
                      </div>
                      開啟地圖
                    </a>
                  </div>
                )}
                {/* Only show customer-safe notes, never show internal_notes */}
                {booking.itinerary.notes && (
                  <div>
                    <p className="text-[11px] font-light text-[#2C2C2C]/40 mb-0.5">攝影師說明</p>
                    <p className="text-sm font-light text-[#2C2C2C]/70 leading-relaxed">{booking.itinerary.notes}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 py-6">
                <div className="w-8 h-8 flex items-center justify-center text-[#C8B8A6]/50">
                  <i className="ri-time-line text-xl" />
                </div>
                <p className="text-sm font-light text-[#2C2C2C]/40">攝影服務集合資訊尚未確定</p>
                <p className="text-xs font-light text-[#2C2C2C]/30">等待安排中，我們會盡快通知您集合地點與時間</p>
              </div>
            )}
          </div>

          <p className="text-center text-xs font-light text-[#2C2C2C]/25 leading-relaxed">
            本攝影服務資訊僅供確認集合安排使用。<br />
            如需更改預訂，請透過平台聯絡客服。
          </p>
        </div>
      )}
    </CustomerLayout>
  );
}
